import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import boletoUtils from '@mrmgomes/boleto-utils';
/* eslint-disable func-names */
import * as yup from 'yup';

const schemaFornecedor = yup
  .object()
  .nullable()
  .required(mensagemCampoObrigatorio('o fornecedor'))
  .test('fornecedor', mensagemCampoObrigatorio('o fornecedor'), function(fornecedor) {
    return !!fornecedor && !!fornecedor?.id;
  });

const schemaCategorias = yup
  .array()
  .min(1)
  .of(
    yup.object().shape({
      classe: yup
        .object()
        .nullable()
        .required(mensagemCampoObrigatorio('a categoria'))
        .test('classe', mensagemCampoObrigatorio('a categoria'), function(classe) {
          return !!classe && !!classe?.id;
        }),
      valorBruto: yup
        .number()
        .nullable()
        .min(0.01, 'O valor bruto deve ser maior que 0')
        .required(mensagemCampoObrigatorio('o valor bruto')),
      descricao: yup.string().nullable(),
    })
  );

const schemaParcelas = yup
  .array()
  .min(1)
  .of(
    yup.object().shape({
      dataVencimento: yup
        .date()
        .nullable()
        .required(mensagemCampoObrigatorio('a data de vecimento')),
      valorLiquido: yup
        .number()
        .nullable()
        .required(mensagemCampoObrigatorio('o valor líquido')),
      conta: yup
        .object()
        .nullable()
        .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
          return this.parent?.contemBaixa ? Boolean(conta) : true;
        }),
      formaPagamento: yup
        .string()
        .nullable()
        .test('formaPagamento', mensagemCampoObrigatorio('a forma do pagamento'), function(formaPagamento) {
          return this.parent?.contemBaixa ? Boolean(formaPagamento) : true;
        }),
      informarCodigoBarras: yup.bool(),
      linhaDigitavel: yup
        .string()
        .nullable()
        .test('linhaDigitavel', mensagemCampoObrigatorio('o código de barras'), function(linhaDigitavel) {
          return this.parent?.informarCodigoBarras ? Boolean(linhaDigitavel) : true;
        }),
    })
  );

/*
  RNE-121 - Obrigatoriedade de retenção : Public <<Functional>> Feature
Created:	24/03/2020 13:52:24
Modified:	03/04/2020 11:25:59
Project:	
Advanced:	
Quando o usuário preencher qualquer campo na aba "Retenções",
 o sistema deve obrigar o preenchimento da "Data do vencimento" e "Imposto".
  Se nenhuma informação de retenção for preenchida, o sistema não deve obrigar esses campos.
*/

const isRetencaoPreenchida = (dados) => {
  const isPreenchido = !!(
    dados.imposto?.id ||
    dados.dataVencimento ||
    (dados.valor !== null && dados.valor !== undefined && dados.valor > 0) ||
    dados.contribuinte ||
    dados.formaPagamento ||
    dados.linhaDigitavel ||
    dados.conta?.id
  );
  return isPreenchido;
};
const schemaRetencoes = yup.array().of(
  yup.object().shape({
    imposto: yup
      .object()
      .nullable()
      .test('imposto', mensagemCampoObrigatorio('o imposto'), function(imposto) {
        return isRetencaoPreenchida(this.parent) ? !!imposto?.id : true;
      }),
    dataVencimento: yup
      .date()
      .nullable()
      .test('dataVencimento', mensagemCampoObrigatorio('a data de vencimento'), function(dataVencimento) {
        return isRetencaoPreenchida(this.parent) ? !!dataVencimento : true;
      }),
    valor: yup
      .number()
      .nullable()
      .test('valor', mensagemCampoObrigatorio('o valor'), function(valor) {
        return isRetencaoPreenchida(this.parent) ? valor !== null && valor !== undefined && valor > 0 : true;
      }),
    contribuinte: yup.string().nullable(),
    formaPagamento: yup.string().nullable(),
    linhaDigitavel: yup.string().nullable(),
  })
);

const schemaRecorrencias = yup.object().shape({
  frequencia: yup
    .string()
    .nullable()
    .test('frequencia', mensagemCampoObrigatorio('a frequência'), function(frequencia) {
      return !this.parent?.repetirLancamento ? true : !!frequencia;
    }),

  quantidade: yup
    .number()
    .nullable()
    .test('quantidade', mensagemCampoObrigatorio('a quantidade'), function(quantidade) {
      return !this.parent?.repetirLancamento ? true : quantidade !== null && quantidade !== undefined && quantidade > 0;
    }),
});

export const schemaAproveitamentoCredito = yup
  .object()
  .shape({
    tipoAproveitamentoCredito: yup
      .string()
      .nullable()
      .when('ativo', {
        is: true,
        then: yup
          .string()
          .nullable()
          .required('Informe o tipo de aproveitamento de crédito'),
      }),
    percentualCredito: yup
      .number()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: 'CREDITO_PARCIAL',
        then: yup
          .number()
          .nullable()
          .required('Informe o percentual de crédito')
          .min(0, 'O percentual de crédito deve ser maior ou igual a 0')
          .max(100, 'O percentual de crédito deve ser menor ou igual a 100'),
      }),
    aliquotaIbs: yup
      .number()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: (value) => ['CREDITO_PARCIAL', 'CREDITO_INTEGRAL'].includes(value),
        then: yup
          .number()
          .nullable()
          .required('Informe a alíquota IBS')
          .min(0, 'O percentual da alíquota deve ser maior ou igual a 0')
          .max(100, 'O percentual da alíquota deve ser menor ou igual a 100'),
      }),
    aliquotaCbs: yup
      .number()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: (value) => ['CREDITO_PARCIAL', 'CREDITO_INTEGRAL'].includes(value),
        then: yup
          .number()
          .nullable()
          .required('Informe a alíquota CBS')
          .min(0, 'O percentual da alíquota deve ser maior ou igual a 0')
          .max(100, 'O percentual da alíquota deve ser menor ou igual a 100'),
      }),
    observacaoJustificativa: yup
      .string()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: 'CREDITO_PARCIAL',
        then: yup
          .string()
          .nullable()
          .max(500)
          .required('Informe a justificativa para aproveitamento de crédito parcial'),
      }),
  })
  .nullable();

const schemaBaixaCadastro = yup.object().shape({
  valorPago: yup
    .number()
    .nullable()
    .test('valorPago', 'O valor pago deve ser maior que zero', function(valorPago) {
      if ((this.parent?.dataPagamento || this.parent?.dataLiquidacao) && (!valorPago || valorPago <= 0)) return false;
      return true;
    }),

  valorEmAberto: yup
    .number()
    .nullable()
    .test('valorEmAberto', 'O valor em aberto não pode ser negativo', function(valorEmAberto) {
      if (this.parent?.dataPagamento && valorEmAberto < 0) return false;
      return true;
    }),

  dataPagamento: yup
    .date()
    .nullable()
    .test('required-pagamento', mensagemCampoObrigatorio('a data do pagamento'), function(dataPagamento) {
      const temValor = this.parent?.valorPago;
      const temLiquidacao = this.parent.dataLiquidacao;
      if ((temValor || temLiquidacao) && !dataPagamento) return false;
      return true;
    })
    .test('dataPagamento', 'A data do pagamento não pode ser superior à liquidação', function(dataPagamento) {
      const dataLiquidacao = this.parent?.dataLiquidacao;
      if (dataLiquidacao && dataPagamento && dataPagamento > dataLiquidacao) return false;
      return true;
    }),

  dataLiquidacao: yup
    .date()
    .nullable()
    .test('required-liquidacao', mensagemCampoObrigatorio('a data da liquidação'), function(dataLiquidacao) {
      const temValor = this.parent?.valorPago;
      const temPagamento = this.parent.dataPagamento;
      if ((temValor || temPagamento) && !dataLiquidacao) return false;
      return true;
    })
    .test('dataLiquidacao', 'A data da liquidação não pode ser inferior ao pagamento', function(dataLiquidacao) {
      const dataPagamento = this.parent?.dataPagamento;
      if (dataPagamento && dataLiquidacao && dataLiquidacao < dataPagamento) return false;
      return true;
    }),
});

const schemaDadosBaixa = yup.object().shape({
  dataPagamento: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data de pagamento')),
  dataLiquidacao: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data da liquidação'))
    .min(yup.ref('dataPagamento'), 'A data de liquidação não pode ser inferior à data de pagamento.'),
  conta: yup
    .object()
    .nullable()
    .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
      // Verifica se conta está vazia olhando para as chaves do objeto, se tiver 0 ele está vazia e se tiver 1 devemos considerar
      // como vazia também, pois a chave 'partnerBank=false' está configurada para vir no objeto base como padrão
      if (
        !conta ||
        (typeof conta === 'object' && (Object.keys(conta).length === 0 || Object.keys(conta).length === 1))
      ) {
        return false;
      }
      return true;
    }),

  formaPagamento: yup
    .string()
    .nullable()
    .test('formaPagamento', mensagemCampoObrigatorio('a forma do pagamento'), function(formaPagamento) {
      return Boolean(formaPagamento);
    }),
  numeroCheque: yup
    .string()
    .nullable()
    .test('numeroCheque', mensagemCampoObrigatorio('o número do cheque'), function(numeroCheque) {
      return this.parent?.formaPagamento === 'CHEQUE' ? Boolean(numeroCheque) : true;
    }),
  valorPago: yup
    .number()
    .nullable()
    .test('valorPago', mensagemCampoObrigatorio('o valor pago'), function(valorPago) {
      return Boolean(valorPago);
    }),
});

const schemaBaixa = yup.object().shape({
  baixa: schemaDadosBaixa,
});

const schemaDespesaUnica = yup.object().shape({
  competencia: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a competência')),

  fornecedor: schemaFornecedor,
  favorecidoDiferenteFornecedor: yup.bool(),
  favorecido: yup
    .object()
    .nullable()
    .test('favorecido', mensagemCampoObrigatorio('o favorecido'), function(favorecido) {
      return this.parent?.favorecidoDiferenteFornecedor ? Boolean(favorecido) : true;
    }),

  categorias: schemaCategorias,
  dataVencimento: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data do vencimento')),

  conta: yup
    .object()
    .nullable()
    .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
      const contaBaixa = this.parent?.baixa?.conta?.id || conta?.id;

      const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;
      const dataLiquidacaoBaixa = this.parent?.baixa?.dataLiquidacao;
      const valorPagoBaixa = this.parent?.baixa?.valorPago;

      if (dataPagamentoBaixa || dataLiquidacaoBaixa || valorPagoBaixa) {
        return !!contaBaixa;
      }
      return true;
    })
    .shape({ id: yup.string() }),
  formaPagamento: yup
    .string()
    .nullable()
    .test('formaPagamento', mensagemCampoObrigatorio('a forma de pagamento'), function(formaPagamento) {
      const formaPagamentoBaixa = this.parent?.baixa?.formaPagamento || formaPagamento;
      const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;
      const dataLiquidacaoBaixa = this.parent?.baixa?.dataLiquidacao;
      const valorPagoBaixa = this.parent?.baixa?.valorPago;

      if (dataPagamentoBaixa || dataLiquidacaoBaixa || valorPagoBaixa) {
        return !!formaPagamentoBaixa;
      }

      return true;
    }),
  documentoFiscal: yup.string().nullable(),
  observacao: yup
    .string()
    .nullable()
    .max(500),
  baixa: schemaBaixaCadastro,
  dataEmissao: yup.object().nullable(),
  linhaDigitavel: yup.string().nullable(),
  retencoes: schemaRetencoes,
  recorrencia: schemaRecorrencias,
  aproveitamentoCredito: schemaAproveitamentoCredito,
});

const schemaLinhaDigitavel = yup.object().shape({
  linhaDigitavel: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o código de barras'))
    .test('linhaDigitavel', 'O código de barras informado é inválido.', function(linhaDigitavel) {
      if (linhaDigitavel) {
        return boletoUtils.validarBoleto(linhaDigitavel).sucesso;
      }
    }),
  fornecedor: schemaFornecedor,
  favorecidoDiferenteFornecedor: yup.bool(),
  favorecido: yup
    .object()
    .nullable()
    .test('favorecido', mensagemCampoObrigatorio('o favorecido'), function(favorecido) {
      return this.parent?.favorecidoDiferenteFornecedor ? Boolean(favorecido) : true;
    }),
  conta: yup
    .object()
    .nullable()
    .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
      const contaBaixa = this.parent?.baixa?.conta?.id || conta?.id;

      const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;
      const dataLiquidacaoBaixa = this.parent?.baixa?.dataLiquidacao;
      const valorPagoBaixa = this.parent?.baixa?.valorPago;

      if (dataPagamentoBaixa || dataLiquidacaoBaixa || valorPagoBaixa) {
        return !!contaBaixa;
      }
      return true;
    })
    .shape({ id: yup.string() }),
  formaPagamento: yup
    .string()
    .nullable()
    .test('formaPagamento', mensagemCampoObrigatorio('a forma de pagamento'), function(formaPagamento) {
      const formaPagamentoBaixa = this.parent?.baixa?.formaPagamento || formaPagamento;
      const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;
      const dataLiquidacaoBaixa = this.parent?.baixa?.dataLiquidacao;
      const valorPagoBaixa = this.parent?.baixa?.valorPago;

      if (dataPagamentoBaixa || dataLiquidacaoBaixa || valorPagoBaixa) {
        return !!formaPagamentoBaixa;
      }

      return true;
    }),
  baixa: schemaBaixaCadastro,
  competencia: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a competência')),
  dataVencimento: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data de vencimento')),
  valorTotal: yup.number().required(mensagemCampoObrigatorio('o valor total')),
  observacao: yup
    .string()
    .nullable()
    .max(500),
  categorias: schemaCategorias,
  retencoes: schemaRetencoes,
  aproveitamentoCredito: schemaAproveitamentoCredito,
});

const schemaDespesaParcelada = yup.object().shape({
  competencia: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a competência')),
  valorTotal: yup
    .number()
    .nullable()
    .required(mensagemCampoObrigatorio('o valor total')),
  numeroParcelas: yup
    .number()
    .nullable()
    .required(mensagemCampoObrigatorio('o número de parcelas'))
    .test('numeroParcelas', mensagemCampoObrigatorio('o número de parcelas'), function(numeroParcelas) {
      return numeroParcelas > 0;
    }),
  dataVencimento: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a data de vencimento')),
  conta: yup
    .object()
    .nullable()
    .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
      const contaBaixa = this.parent?.baixa?.conta?.id || conta?.id;

      const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;
      const dataLiquidacaoBaixa = this.parent?.baixa?.dataLiquidacao;
      const valorPagoBaixa = this.parent?.baixa?.valorPago;

      if (dataPagamentoBaixa || dataLiquidacaoBaixa || valorPagoBaixa) {
        return !!contaBaixa;
      }
      return true;
    })
    .shape({ id: yup.string() }),
  formaPagamento: yup
    .string()
    .nullable()
    .test('formaPagamento', mensagemCampoObrigatorio('a forma de pagamento'), function(formaPagamento) {
      const formaPagamentoBaixa = this.parent?.baixa?.formaPagamento || formaPagamento;
      const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;
      const dataLiquidacaoBaixa = this.parent?.baixa?.dataLiquidacao;
      const valorPagoBaixa = this.parent?.baixa?.valorPago;

      if (dataPagamentoBaixa || dataLiquidacaoBaixa || valorPagoBaixa) {
        return !!formaPagamentoBaixa;
      }

      return true;
    }),
  observacao: yup.string().max(500),
  fornecedor: schemaFornecedor,
  categorias: schemaCategorias,
  parcelamento: yup.object().shape({ parcelas: schemaParcelas }),
  retencoes: schemaRetencoes,
  baixa: schemaBaixaCadastro,
  aproveitamentoCredito: schemaAproveitamentoCredito,
});

const CONTAS_PAGAR_SCHEMA = {
  UNICA: schemaDespesaUnica,
  PARCELADA: schemaDespesaParcelada,
  LINHA_DIGITAVEL: schemaLinhaDigitavel,
  BAIXA: schemaBaixa,
};

export default CONTAS_PAGAR_SCHEMA;
